import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Brain, TrendingUp, Target, Calendar, Clock,
  AlertTriangle, CheckCircle, Loader2, BookOpen, Zap,
  Heart, Battery, Moon, Coffee, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { OposaiLogo } from '@/components/TuPlazaIALogo';
import MLAnalyticsDashboard from '@/components/MLAnalyticsDashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudyPlanPage = () => {
  const navigate = useNavigate();
  const [prediction, setPrediction] = useState(null);
  const [studyPlan, setStudyPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [showBurnoutModal, setShowBurnoutModal] = useState(false);
  const [activeTab, setActiveTab] = useState('plan'); // 'plan', 'prediction', 'analytics'
  const [burnoutForm, setBurnoutForm] = useState({
    stress_level: 5,
    sleep_hours: 7,
    motivation_level: 5,
    study_hours_today: 2
  });
  const [burnoutResult, setBurnoutResult] = useState(null);
  const [targetDate, setTargetDate] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState(2);
  const [daysPerWeek, setDaysPerWeek] = useState(7);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [predRes, planRes] = await Promise.all([
        axios.get(`${API}/adaptive/prediction`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/adaptive/study-plan`, { headers }).catch(() => ({ data: { plan: null } }))
      ]);

      setPrediction(predRes.data);
      setStudyPlan(planRes.data?.plan);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateStudyPlan = async () => {
    if (!targetDate) {
      toast.error('Selecciona una fecha objetivo para el examen');
      return;
    }

    setGeneratingPlan(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/adaptive/study-plan/generate?target_date=${targetDate}&hours_per_day=${hoursPerDay}&days_per_week=${daysPerWeek}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStudyPlan(response.data);
      toast.success('¡Plan de estudio generado!');
    } catch (error) {
      console.error('Error generating study plan:', error);
      toast.error('Error al generar el plan');
    } finally {
      setGeneratingPlan(false);
    }
  };

  const checkBurnout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/adaptive/burnout/check`,
        burnoutForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBurnoutResult(response.data);
    } catch (error) {
      toast.error('Error al verificar estado');
    }
  };

  const getRiskColor = (risk) => {
    if (risk === 'alto') return 'red';
    if (risk === 'medio') return 'amber';
    return 'emerald';
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 70) return 'emerald';
    if (prob >= 50) return 'amber';
    return 'red';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Nav */}
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <OposaiLogo size={40} />
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Brain className="w-5 h-5 text-indigo-600" />
                  Plan de Estudio IA
                </h1>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowBurnoutModal(true)}
              className="rounded-full"
            >
              <Heart className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Check Bienestar</span>
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Tab Selector - 3 pestañas unificadas */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            variant={activeTab === 'plan' ? 'default' : 'outline'}
            onClick={() => setActiveTab('plan')}
            className="rounded-full whitespace-nowrap"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Mi Plan Semanal
          </Button>
          <Button
            variant={activeTab === 'prediction' ? 'default' : 'outline'}
            onClick={() => setActiveTab('prediction')}
            className="rounded-full whitespace-nowrap"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Predicción IA
          </Button>
          <Button
            variant={activeTab === 'analytics' ? 'default' : 'outline'}
            onClick={() => setActiveTab('analytics')}
            className="rounded-full whitespace-nowrap"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Análisis Detallado
          </Button>
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <MLAnalyticsDashboard />
        )}

        {/* Prediction Tab */}
        {activeTab === 'prediction' && (
          <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 mb-8">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Brain className="w-5 h-5 text-indigo-600" />
              Predicción de Resultados con IA
            </h2>

            {prediction?.prediction === null ? (
              <div className="text-center py-8 bg-slate-50 rounded-2xl">
                <Brain className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-2">{prediction?.message || 'Necesitas completar más exámenes para obtener una predicción'}</p>
                <Button onClick={() => navigate('/dashboard')} className="mt-4 rounded-full">
                  Ir a practicar
                </Button>
              </div>
            ) : prediction ? (
              <div className="grid md:grid-cols-3 gap-6">
                {/* Main Prediction */}
                <div className="md:col-span-2">
                  <div className={`bg-gradient-to-br from-${getProbabilityColor(prediction.pass_probability)}-50 to-${getProbabilityColor(prediction.pass_probability)}-100 rounded-2xl p-6`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Probabilidad de aprobar</p>
                        <p className={`text-5xl font-bold text-${getProbabilityColor(prediction.pass_probability)}-600`}>
                          {prediction.pass_probability}%
                        </p>
                      </div>
                      <div className={`w-20 h-20 rounded-full bg-${getProbabilityColor(prediction.pass_probability)}-200 flex items-center justify-center`}>
                        {prediction.pass_probability >= 70 ? (
                          <CheckCircle className={`w-10 h-10 text-${getProbabilityColor(prediction.pass_probability)}-600`} />
                        ) : (
                          <AlertTriangle className={`w-10 h-10 text-${getProbabilityColor(prediction.pass_probability)}-600`} />
                        )}
                      </div>
                    </div>
                    <div className="bg-white/50 rounded-xl p-4">
                      <p className="text-sm font-medium text-slate-700">{prediction.recommendation}</p>
                    </div>
                  </div>

                  {/* Factors */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-slate-900">{prediction.factors?.average_score}%</p>
                      <p className="text-xs text-slate-500">Media</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-slate-900">{prediction.factors?.total_exams}</p>
                      <p className="text-xs text-slate-500">Exámenes</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-slate-900">{prediction.factors?.streak_days}d</p>
                      <p className="text-xs text-slate-500">Racha</p>
                    </div>
                  </div>
                </div>

                {/* Weak Topics */}
                <div>
                  <h3 className="font-bold text-sm mb-3 text-slate-700">Temas a reforzar ({prediction.weak_topics?.length || 0})</h3>
                  {prediction.weak_topics?.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                      {prediction.weak_topics.map((topic, idx) => (
                        <div key={idx} className="bg-red-50 rounded-lg p-3">
                          <p className="text-sm font-medium text-slate-900">{topic.topic_name || topic.name || 'Tema'}</p>
                          <div className="flex items-center justify-between mt-1">
                            <div className="flex-1 bg-slate-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-red-500 h-2 rounded-full" 
                                style={{ width: `${Math.max(topic.accuracy || topic.success_rate || 0, 5)}%` }} 
                              />
                            </div>
                            <span className="text-xs text-red-600 font-medium">{Math.round(topic.accuracy || topic.success_rate || 0)}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Completa más exámenes para ver temas débiles</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-600">Cargando predicción...</p>
              </div>
            )}
          </div>
        )}

        {/* Plan Tab */}
        {activeTab === 'plan' && (
        <>
        {/* Study Plan Generator */}
        <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 mb-8">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600" />
            Generar Plan de Estudio Personalizado
          </h2>

          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Fecha del examen
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Horas de estudio al día
              </label>
              <select
                value={hoursPerDay}
                onChange={(e) => setHoursPerDay(parseFloat(e.target.value))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
              >
                <option value={1}>1 hora</option>
                <option value={1.5}>1.5 horas</option>
                <option value={2}>2 horas</option>
                <option value={2.5}>2.5 horas</option>
                <option value={3}>3 horas</option>
                <option value={4}>4 horas</option>
                <option value={5}>5+ horas</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Días de estudio/semana
              </label>
              <select
                value={daysPerWeek}
                onChange={(e) => setDaysPerWeek(parseInt(e.target.value))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
              >
                <option value={7}>7 días (todos)</option>
                <option value={6}>6 días (Lun-Sáb)</option>
                <option value={5}>5 días (Lun-Vie)</option>
                <option value={4}>4 días</option>
                <option value={3}>3 días (alternos)</option>
                <option value={2}>2 días</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={generateStudyPlan}
                disabled={generatingPlan}
                className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white py-6"
              >
                {generatingPlan ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Generar Plan
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Current Study Plan */}
          {studyPlan && (
            <div className="mt-6">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 mb-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-bold">Tu Plan Actual</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('session_token');
                        const response = await axios.get(`${API}/adaptive/study-plan/download`, {
                          headers: { Authorization: `Bearer ${token}` },
                          responseType: 'blob'
                        });
                        
                        // Check if the response is an error (JSON instead of PDF)
                        if (response.data.type === 'application/json') {
                          const reader = new FileReader();
                          reader.onload = () => {
                            const error = JSON.parse(reader.result);
                            toast.error(error.detail || 'Error al descargar');
                          };
                          reader.readAsText(response.data);
                          return;
                        }
                        
                        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'plan_estudio.pdf');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('Plan descargado correctamente');
                      } catch (error) {
                        const errorMessage = error.response?.data?.detail || 'Primero genera un plan de estudio seleccionando una fecha de examen';
                        toast.error(errorMessage);
                      }
                    }}
                    className="rounded-full"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Descargar PDF
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-indigo-600">
                      {studyPlan.summary?.days_until_exam || studyPlan.days_until_exam}
                    </p>
                    <p className="text-sm text-slate-600">Días</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-600">
                      {studyPlan.summary?.total_study_hours || studyPlan.total_hours}h
                    </p>
                    <p className="text-sm text-slate-600">Total horas</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-600">
                      {studyPlan.summary?.hours_per_day || studyPlan.hours_per_day}h
                    </p>
                    <p className="text-sm text-slate-600">Por día</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-amber-600">
                      {(studyPlan.weeks || []).length}
                    </p>
                    <p className="text-sm text-slate-600">Semanas</p>
                  </div>
                </div>
              </div>

              {/* Phases */}
              {studyPlan.phases && (
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {studyPlan.phases.map((phase, idx) => (
                    <div key={idx} className={`rounded-xl p-4 ${
                      idx === 0 ? 'bg-blue-50 border border-blue-200' :
                      idx === 1 ? 'bg-amber-50 border border-amber-200' :
                      'bg-emerald-50 border border-emerald-200'
                    }`}>
                      <h4 className="font-bold">{phase.name}</h4>
                      <p className="text-sm text-slate-600">{phase.duration_days} días ({phase.percentage}%)</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Weekly Plan - ALL WEEKS */}
              <h3 className="font-bold mb-4">Plan Semanal Completo ({(studyPlan.weeks || []).length} semanas)</h3>
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {(studyPlan.weeks || []).map((week, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center font-bold text-indigo-600">
                          {week.week}
                        </span>
                        <div>
                          <span className="font-bold text-slate-900">Semana {week.week}</span>
                          <p className="text-xs text-slate-500">{week.days} días · {week.hours_total}h total</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        week.phase?.includes('1ª') ? 'bg-blue-100 text-blue-700' :
                        week.phase?.includes('2ª') ? 'bg-amber-100 text-amber-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {week.phase}
                      </span>
                    </div>
                    
                    <p className="text-sm text-slate-700 mb-3 font-medium">{week.focus}</p>
                    <p className="text-sm text-slate-600 mb-3">🎯 {week.objective}</p>
                    
                    {/* Activities breakdown */}
                    {week.activities && (
                      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100">
                        <div className="text-xs">
                          <span className="text-slate-500">📖 Estudio:</span>
                          <p className="text-slate-700">{week.activities.estudio}</p>
                        </div>
                        <div className="text-xs">
                          <span className="text-slate-500">🧠 Tests:</span>
                          <p className="text-slate-700">{week.activities.tests}</p>
                        </div>
                        <div className="text-xs">
                          <span className="text-slate-500">🃏 Flashcards:</span>
                          <p className="text-slate-700">{week.activities.flashcards}</p>
                        </div>
                        <div className="text-xs">
                          <span className="text-slate-500">🏆 Simulacro:</span>
                          <p className="text-slate-700">{week.activities.simulacro}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Topics covered */}
                    {week.topics_covered?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-500 mb-2">Temas de esta semana:</p>
                        <div className="flex flex-wrap gap-1">
                          {week.topics_covered.slice(0, 6).map((topic, i) => (
                            <span key={i} className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600">
                              {topic.length > 40 ? topic.substring(0, 40) + '...' : topic}
                            </span>
                          ))}
                          {week.topics_covered.length > 6 && (
                            <span className="text-xs text-slate-400">+{week.topics_covered.length - 6} más</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {/* Burnout Check Modal */}
      {showBurnoutModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowBurnoutModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500" />
              Check de Bienestar
            </h2>

            {!burnoutResult ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
                    <Battery className="w-4 h-4" />
                    Nivel de estrés (1-10)
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={burnoutForm.stress_level}
                    onChange={(e) => setBurnoutForm({...burnoutForm, stress_level: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Relajado</span>
                    <span className="font-bold">{burnoutForm.stress_level}</span>
                    <span>Muy estresado</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
                    <Moon className="w-4 h-4" />
                    Horas de sueño anoche
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="12"
                    step="0.5"
                    value={burnoutForm.sleep_hours}
                    onChange={(e) => setBurnoutForm({...burnoutForm, sleep_hours: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 rounded-xl border border-slate-200"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4" />
                    Nivel de motivación (1-10)
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={burnoutForm.motivation_level}
                    onChange={(e) => setBurnoutForm({...burnoutForm, motivation_level: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Sin ganas</span>
                    <span className="font-bold">{burnoutForm.motivation_level}</span>
                    <span>Muy motivado</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
                    <Coffee className="w-4 h-4" />
                    Horas de estudio hoy
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="16"
                    step="0.5"
                    value={burnoutForm.study_hours_today}
                    onChange={(e) => setBurnoutForm({...burnoutForm, study_hours_today: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 rounded-xl border border-slate-200"
                  />
                </div>

                <Button
                  onClick={checkBurnout}
                  className="w-full rounded-full bg-pink-600 hover:bg-pink-700 text-white"
                >
                  Analizar
                </Button>
              </div>
            ) : (
              <div>
                <div className={`rounded-2xl p-6 mb-4 ${
                  burnoutResult.risk_level === 'alto' ? 'bg-red-50' :
                  burnoutResult.risk_level === 'medio' ? 'bg-amber-50' : 'bg-emerald-50'
                }`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-slate-600">Riesgo de burnout</p>
                      <p className={`text-3xl font-bold text-${getRiskColor(burnoutResult.risk_level)}-600 capitalize`}>
                        {burnoutResult.risk_level}
                      </p>
                    </div>
                    <div className={`w-16 h-16 rounded-full bg-${getRiskColor(burnoutResult.risk_level)}-200 flex items-center justify-center`}>
                      <span className="text-2xl font-bold text-${getRiskColor(burnoutResult.risk_level)}-600">
                        {burnoutResult.risk_score}
                      </span>
                    </div>
                  </div>

                  {burnoutResult.risk_factors?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium mb-2">Factores de riesgo:</p>
                      <ul className="space-y-1">
                        {burnoutResult.risk_factors.map((factor, i) => (
                          <li key={i} className="text-sm text-slate-700">• {factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium mb-2">Recomendaciones:</p>
                  <ul className="space-y-1">
                    {burnoutResult.recommendations?.map((rec, i) => (
                      <li key={i} className="text-sm">{rec}</li>
                    ))}
                  </ul>
                </div>

                <Button
                  onClick={() => {
                    setBurnoutResult(null);
                    setShowBurnoutModal(false);
                  }}
                  variant="outline"
                  className="w-full rounded-full"
                >
                  Cerrar
                </Button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default StudyPlanPage;
